import type { FragmentType } from '@/generated/gql/fragment-masking'
import { useFragment } from '@/generated/gql/fragment-masking'
import { graphql } from '@/generated/gql/gql'
import Close from '@mui/icons-material/Close'
import { IconButton, Stack, Typography } from '@mui/material'

import React from 'react'
import { useTranslation } from 'react-i18next'

import LocationSelectionGraphql from '../../LocationSelectionGraphql'
import { TimeslotSelectionGraphql } from '../../TimeslotSelectionGraphql'
import CurrentCredits from '../CurrentCredits'

const canteenCartHeaderUserDataFragment = graphql(`
  fragment CanteenCartHeader_UserDataFragment on User {
    accountLocation {
      timezone
    }
    ...CanteenLocationSelection_UserDataFragment
    ...CanteenCartCurrentCredits_UserDataFragment
    account {
      userInterfaceSettings {
        hideCCPrices
      }
    }
  }
`)

type Props = {
  arrivalTime: string
  availableSubsidy: number
  timeslots: { id: string; startTime: string; endTime: string }[]
  userData: FragmentType<typeof canteenCartHeaderUserDataFragment>
  onClose: () => void
  onChangeTimeslot: (timeslot: string) => void
}

const Header: React.FC<Props> = ({
  onClose,
  onChangeTimeslot,
  arrivalTime,
  availableSubsidy,
  timeslots,
  userData: userDataProp,
}) => {
  const userData = useFragment(canteenCartHeaderUserDataFragment, userDataProp)
  const { t } = useTranslation()

  return (
    <>
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        justifyContent="space-between"
        padding={1}
        paddingRight={2}>
        <IconButton onClick={onClose} data-cy="order-modal-close-button">
          <Close />
        </IconButton>
        <Typography variant="subtitle2">{t('cc:yourOrder')}</Typography>
        {!userData.account?.userInterfaceSettings.hideCCPrices ? (
          <CurrentCredits userData={userData} availableSubsidy={availableSubsidy} />
        ) : null}
      </Stack>
      <Stack
        bgcolor="background.default"
        padding={1}
        direction={{ xs: 'column', md: 'row' }}
        spacing={1}>
        <LocationSelectionGraphql userData={userData} disabled />
        <TimeslotSelectionGraphql
          timezone={userData.accountLocation?.timezone}
          selectedTimeslot={arrivalTime}
          timeslots={timeslots}
          onChangeTimeslot={onChangeTimeslot}
        />
      </Stack>
    </>
  )
}

export default Header
